import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import axios from 'axios';
import {
  Wallet,
  TrendingUp,
  Coins,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  AlertCircle,
  Shield,
  Trophy,
  CreditCard,
  ArrowDownToLine
} from 'lucide-react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [yieldRankings, setYieldRankings] = useState([]);
  const [popularRankings, setPopularRankings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dashRes, yieldRes, popularRes] = await Promise.all([
          axios.get(`${API_URL}/api/dashboard`, { withCredentials: true }),
          axios.get(`${API_URL}/api/rankings/yield`),
          axios.get(`${API_URL}/api/rankings/popular`)
        ]);
        setDashboardData(dashRes.data);
        setYieldRankings(yieldRes.data);
        setPopularRankings(popularRes.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY',
      maximumFractionDigits: 0
    }).format(value);
  };

  const getFundName = (fund) => {
    if (i18n.language === 'en' && fund.name_en) return fund.name_en;
    if (i18n.language === 'pt' && fund.name_pt) return fund.name_pt;
    return fund.name || fund.fund_name;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#1C1C1C] border border-white/10 rounded-sm p-3">
          <p className="text-zinc-400 text-xs">{label}</p>
          <p className="data-value text-white">{formatCurrency(payload[0].value)}</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32 bg-[#141414]" />
            ))}
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            <Skeleton className="h-80 lg:col-span-2 bg-[#141414]" />
            <Skeleton className="h-80 bg-[#141414]" />
          </div>
        </div>
      </div>
    );
  }

  const stats = [
    {
      label: t('deposit.balance'),
      value: formatCurrency(dashboardData?.balance || 0),
      icon: Wallet,
      trend: null
    },
    {
      label: t('dashboard.totalAssets'),
      value: formatCurrency(dashboardData?.total_assets || 0),
      icon: TrendingUp,
      trend: null
    },
    {
      label: t('dashboard.totalYield'),
      value: `${dashboardData?.total_yield || 0}%`,
      icon: TrendingUp,
      trend: dashboardData?.total_yield > 0 ? 'up' : dashboardData?.total_yield < 0 ? 'down' : null,
      color: dashboardData?.total_yield >= 0 ? 'text-emerald-500' : 'text-red-500'
    },
    {
      label: t('dashboard.totalDistributions'),
      value: formatCurrency(dashboardData?.total_distributions || 0),
      icon: Coins,
      trend: null
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl text-white">{t('dashboard.title')}</h1>
            <p className="text-zinc-500 mt-1">{user?.name}</p>
          </div>
          
          {/* KYC Status */}
          {!user?.is_kyc_verified && (
            <Link to="/kyc">
              <Button className="bg-amber-500/10 text-amber-500 border border-amber-500/30 hover:bg-amber-500/20 rounded-sm" data-testid="kyc-warning">
                <AlertCircle className="h-4 w-4 mr-2" />
                {t('kyc.required')}
              </Button>
            </Link>
          )}
          {user?.is_kyc_verified && (
            <div className="flex items-center gap-2 text-emerald-500" data-testid="kyc-verified">
              <Shield className="h-4 w-4" />
              <span className="text-sm">{t('kyc.verified')}</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mb-8">
          <Link to="/deposit">
            <Button className="bg-emerald-500/10 text-emerald-500 border border-emerald-500/30 hover:bg-emerald-500/20 rounded-sm" data-testid="deposit-button">
              <CreditCard className="h-4 w-4 mr-2" />
              {t('dashboard.deposit')}
            </Button>
          </Link>
          <Link to="/withdrawal">
            <Button className="bg-white/5 text-white border border-white/20 hover:bg-white/10 rounded-sm" data-testid="withdrawal-button">
              <ArrowDownToLine className="h-4 w-4 mr-2" />
              {t('dashboard.withdraw')}
            </Button>
          </Link>
          <Link to="/funds">
            <Button className="bg-white text-black hover:bg-zinc-200 rounded-sm" data-testid="invest-button">
              {t('dashboard.invest')}
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="card-presage rounded-sm" data-testid={`stat-card-${index}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <stat.icon className="h-5 w-5 text-zinc-500" />
                    {stat.trend && (
                      stat.trend === 'up' ? (
                        <ArrowUpRight className="h-4 w-4 text-emerald-500" />
                      ) : (
                        <ArrowDownRight className="h-4 w-4 text-red-500" />
                      )
                    )}
                  </div>
                  <p className="data-label mt-4">{stat.label}</p>
                  <p className={`data-value text-2xl mt-1 ${stat.color || 'text-white'}`}>
                    {stat.value}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-3 gap-8 mb-8">
          {/* Asset History Chart */}
          <Card className="card-presage rounded-sm lg:col-span-2">
            <CardContent className="p-6">
              <h3 className="text-white font-semibold mb-6">{t('dashboard.assetHistory')}</h3>
              {dashboardData?.asset_history && dashboardData.asset_history.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={dashboardData.asset_history}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#71717A"
                      tick={{ fill: '#71717A', fontSize: 12 }}
                    />
                    <YAxis 
                      stroke="#71717A"
                      tick={{ fill: '#71717A', fontSize: 12 }}
                      tickFormatter={(value) => `¥${(value / 10000).toFixed(0)}K`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#10B981" 
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-zinc-500">
                  {t('dashboard.noInvestments')}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Rankings */}
          <Card className="card-presage rounded-sm">
            <CardContent className="p-6">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Trophy className="h-4 w-4 text-[#D4AF37]" />
                {t('dashboard.rankings')}
              </h3>
              <Tabs defaultValue="yield" className="w-full">
                <TabsList className="grid w-full grid-cols-2 bg-[#0A0A0A]">
                  <TabsTrigger value="yield" className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-400 rounded-sm">
                    {t('dashboard.highYield')}
                  </TabsTrigger>
                  <TabsTrigger value="popular" className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-400 rounded-sm">
                    {t('dashboard.popular')}
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="yield" className="mt-4 space-y-3">
                  {yieldRankings.slice(0, 5).map((fund) => (
                    <Link key={fund.id} to={`/funds/${fund.id}`}>
                      <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-sm hover:bg-white/5 transition-colors" data-testid={`ranking-yield-${fund.id}`}>
                        <div className="flex items-center gap-3">
                          <span className="data-value text-[#D4AF37] w-6">#{fund.rank}</span>
                          <span className="text-white text-sm truncate max-w-[120px]">{getFundName(fund)}</span>
                        </div>
                        <span className="data-value text-emerald-500">{fund.target_yield}%</span>
                      </div>
                    </Link>
                  ))}
                </TabsContent>
                <TabsContent value="popular" className="mt-4 space-y-3">
                  {popularRankings.slice(0, 5).map((fund) => (
                    <Link key={fund.id} to={`/funds/${fund.id}`}>
                      <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-sm hover:bg-white/5 transition-colors" data-testid={`ranking-popular-${fund.id}`}>
                        <div className="flex items-center gap-3">
                          <span className="data-value text-[#D4AF37] w-6">#{fund.rank}</span>
                          <span className="text-white text-sm truncate max-w-[120px]">{getFundName(fund)}</span>
                        </div>
                        <span className="data-value text-zinc-400">¥{(fund.total_raised / 100000000).toFixed(1)}億</span>
                      </div>
                    </Link>
                  ))}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* My Investments */}
        <Card className="card-presage rounded-sm">
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-white font-semibold">{t('dashboard.myInvestments')}</h3>
              <Link to="/funds">
                <Button variant="ghost" className="text-zinc-400 hover:text-white text-sm" data-testid="start-investing">
                  {t('dashboard.startInvesting')}
                </Button>
              </Link>
            </div>
            
            {dashboardData?.investments && dashboardData.investments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="data-label text-left py-3 px-2">{t('funds.title')}</th>
                      <th className="data-label text-right py-3 px-2">{t('funds.minInvestment')}</th>
                      <th className="data-label text-right py-3 px-2">Current Value</th>
                      <th className="data-label text-right py-3 px-2">{t('dashboard.totalDistributions')}</th>
                      <th className="data-label text-right py-3 px-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.investments.map((inv) => (
                      <tr key={inv.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`investment-${inv.id}`}>
                        <td className="py-4 px-2">
                          <Link to={`/funds/${inv.fund_id}`} className="text-white hover:underline">
                            {inv.fund_name}
                          </Link>
                        </td>
                        <td className="data-value text-right py-4 px-2 text-zinc-400">
                          {formatCurrency(inv.amount)}
                        </td>
                        <td className="data-value text-right py-4 px-2 text-white">
                          {formatCurrency(inv.current_value)}
                        </td>
                        <td className="data-value text-right py-4 px-2 text-emerald-500">
                          {formatCurrency(inv.total_distributions)}
                        </td>
                        <td className="text-right py-4 px-2">
                          <span className={`px-2 py-1 rounded-sm text-xs ${
                            inv.status === 'active' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-zinc-500/10 text-zinc-500'
                          }`}>
                            {inv.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <Wallet className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500 mb-4">{t('dashboard.noInvestments')}</p>
                <Link to="/funds">
                  <Button className="bg-white text-black hover:bg-zinc-200 rounded-sm">
                    {t('dashboard.startInvesting')}
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
